import { merge } from './merger';
import { unmerge } from './unmerger';
import { cleanSmartQuotesAndPunctuation } from './typography';
import type { ConversionMode, ConversionOptions, ConversionResult } from './types';

/**
 * Unified facade for converting LRC content between merged and unmerged formats.
 *
 * Automatically normalizes curved smart quotes, curly apostrophes, dashes, and ellipses
 * by default (options.cleanPunctuation !== false) for maximum media player compatibility.
 *
 * @param rawText Raw LRC string content.
 * @param mode Conversion direction: 'merge' (to shared timestamps) or 'unmerge' (to chronological).
 * @param options Conversion options (blank handling, symbol, spacing, whitespace cleanup, cleanPunctuation).
 */
export function convertLrc(
  rawText: string,
  mode: ConversionMode,
  options: ConversionOptions = {}
): ConversionResult {
  if (!rawText || !rawText.trim()) {
    return {
      text: '',
      inputLines: 0,
      outputLines: 0,
      blankCount: 0,
      durationMs: 0
    };
  }

  const shouldCleanPunctuation = options.cleanPunctuation !== false;
  const processedText = shouldCleanPunctuation
    ? cleanSmartQuotesAndPunctuation(rawText).text
    : rawText;

  return mode === 'unmerge'
    ? unmerge(processedText, options)
    : merge(processedText, options);
}
